import { LockMixin } from "apps-module/app/shared-library/mixins/lock.mixin";
import { EmptyInstance } from "core-library/mixins/empty-instance";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { BehaviorSubject } from "rxjs";
import { SpinnerComponent } from "widgets-module/components/spinner/spinner.component";
import { DestroyableMixin } from "widgets-module/mixins/destroyable.mixin";

// A4 at 96 DPI, matching the "px" unit jsPDF uses below.
const PDF_PAGE_WIDTH_PX = 794;
const PDF_PAGE_HEIGHT_PX = 1123;
const CAPTURE_SCALE = 2;
const JPEG_QUALITY = 0.85;

export class PageExportService extends DestroyableMixin(LockMixin(EmptyInstance)) {
    private readonly classMap = {
        exportButton: ".js-export-button"
    };

    private _currentContext: JQuery = $();

    private _exportButton: JQuery = $();

    private _spinnerComponent!: SpinnerComponent;

    private _stateSubject = new BehaviorSubject<boolean>(false);

    private parseContext(): void {
        this._exportButton = $(this.classMap.exportButton, this._currentContext);

        const spinnerComponentContext = $(SpinnerComponent.SELECTOR, this._currentContext);
        this._spinnerComponent = SpinnerComponent.init(spinnerComponentContext);
    }

    private bindEvents(): void {
        this._exportButton.on("click", (e) => {
            e.preventDefault();

            void this.export(window);
        });

        this._stateSubject
            .subscribe((state: boolean) => {
                if (state) {
                    this._spinnerComponent.show();
                } else {
                    this._spinnerComponent.hide();
                }
            });
    }

    private async export(aggregateWindow: Window): Promise<void> {
        this.toggleSpinner(true);

        const collapsingElements = $(".collapse", aggregateWindow.document);
        const collapsedElements = $(".collapsed", aggregateWindow.document);

        this.toggleCollapsibleElements(collapsingElements, collapsedElements, true);

        // Let the browser reflow the newly-expanded collapsibles before we
        // measure/capture them, otherwise html2canvas can grab a pre-layout
        // frame where content past the first block hasn't expanded yet.
        await this.waitForNextFrame();

        const pages = $(".pcg-main-content", aggregateWindow.document);

        const pdf = new jsPDF({
            orientation: "p",
            unit: "px",
            format: [PDF_PAGE_WIDTH_PX, PDF_PAGE_HEIGHT_PX]
        });

        let isFirstPage = true;

        for (const page of pages) {
            const canvas = await html2canvas(page, {
                scrollX: 0,
                scrollY: -window.scrollY,
                scale: CAPTURE_SCALE,
                useCORS: true
            });

            isFirstPage = this.addCanvasToPdf(pdf, canvas, isFirstPage);
        }

        const url = window.location.hostname;
        const now = new Date();
        const datetime = now.toISOString().replace(/:/g, "-");
        const filename = `${url}_${datetime}.pdf`;

        pdf.save(filename);

        window.close();

        this.toggleCollapsibleElements(collapsingElements, collapsedElements, false);

        this.toggleSpinner(false);
    }

    private waitForNextFrame(): Promise<void> {
        return new Promise((resolve) => {
            requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
        });
    }

    /**
     * Slices a (potentially very tall) captured canvas into PDF_PAGE_HEIGHT_PX-tall
     * chunks so long articles paginate normally instead of producing a single
     * page as tall as the whole article (which risks hitting browser canvas
     * size limits and produces oversized PNG output).
     */
    private addCanvasToPdf(pdf: jsPDF, canvas: HTMLCanvasElement, isFirstPage: boolean): boolean {
        const pxToPdfRatio = PDF_PAGE_WIDTH_PX / canvas.width;
        const sliceHeightPx = Math.floor(PDF_PAGE_HEIGHT_PX / pxToPdfRatio);

        let offsetY = 0;
        let firstPage = isFirstPage;

        while (offsetY < canvas.height) {
            const currentSliceHeightPx = Math.min(sliceHeightPx, canvas.height - offsetY);

            const sliceCanvas = document.createElement("canvas");
            sliceCanvas.width = canvas.width;
            sliceCanvas.height = currentSliceHeightPx;

            const context = sliceCanvas.getContext("2d");

            if (context) {
                context.drawImage(
                    canvas,
                    0, offsetY, canvas.width, currentSliceHeightPx,
                    0, 0, canvas.width, currentSliceHeightPx
                );

                if (!firstPage) {
                    pdf.addPage();
                }

                const imgData = sliceCanvas.toDataURL("image/jpeg", JPEG_QUALITY);
                const imgHeightPx = currentSliceHeightPx * pxToPdfRatio;

                pdf.addImage(imgData, "JPEG", 0, 0, PDF_PAGE_WIDTH_PX, imgHeightPx);

                firstPage = false;
            }

            offsetY += currentSliceHeightPx;
        }

        return firstPage;
    }

    private toggleCollapsibleElements(collapsingElements: JQuery<HTMLElement>, collapsedElements: JQuery<HTMLElement>, show: boolean): void {
        collapsingElements.each((_, element) => {
            if (show) {
                $(element).addClass("show");
            } else {
                $(element).removeClass("show");
            }
        });

        collapsedElements.each((_, element) => {
            if (show) {
                $(element).removeClass("collapsed");
            } else {
                $(element).addClass("collapsed");
            }
        });
    }

    private toggleSpinner(state: boolean): void {
        if (state) {
            this.lock();
        } else {
            this.unlock();
        }

        this._stateSubject.next(this.isLocked);
    }

    public init(context: JQuery): void {
        this._currentContext = context || this._currentContext;

        this.parseContext();
        this.bindEvents();
    }
}
